/**
 * Public API surface of the Consenteer SDK.
 * Inspired by silktide/consent-manager (cf. docs/decisions.md ADR-010).
 */

import { ApiClient, DomainNotAuthorizedError, hashSettings } from "./api-client.js";
import {
  armKnownAutoBlock,
  disarmAndRelease,
  setKnownAutoBlockGrants,
} from "./auto-block-known.js";
import { activateBlockedScripts } from "./auto-block.js";
import { type BannerHandle, createBanner } from "./banner.js";
import { purgeRefusedCookies } from "./cookie-cleanup.js";
import { emitConsentEvent } from "./events.js";
import { declareDefaults, updateFromChoices } from "./gcm.js";
import { CANONICAL_API_URL } from "./origin.js";
import { ConsentStorage } from "./storage.js";
import { applyTheme } from "./theme.js";
import type {
  BannerSettings,
  ConsentChoices,
  CsteerConfig,
  CsteerInstance,
  WebstatEvent,
} from "./types.js";

const DEFAULT_API_URL = CANONICAL_API_URL;
const DEFAULT_NAMESPACE = "csteer";

let instance: CsteerInstance | null = null;
let activeBanner: BannerHandle | null = null;

// Declare GCM v2 defaults at module load — before any user-triggered init.
declareDefaults();

function normalizeConfig(config: CsteerConfig): CsteerInstance["config"] {
  return {
    ...config,
    siteId: config.siteId,
    apiUrl: config.apiUrl ?? DEFAULT_API_URL,
    namespace: config.namespace ?? DEFAULT_NAMESPACE,
  };
}

/**
 * Session-scoped dismissal: the visitor closed the banner (X / Escape)
 * without answering. We remember it in sessionStorage so page navigations in
 * the same tab don't re-open the banner (only the floating icon is mounted).
 * No consent is stored and trackers stay blocked — the next visit asks again.
 */
function dismissKey(namespace: string, siteId: string): string {
  return `${namespace}:${siteId}:dismissed`;
}
function rememberDismissed(namespace: string, siteId: string): void {
  try {
    sessionStorage.setItem(dismissKey(namespace, siteId), "1");
  } catch {
    /* storage unavailable — the banner will simply re-open */
  }
}
function isDismissedThisSession(namespace: string, siteId: string): boolean {
  try {
    return sessionStorage.getItem(dismissKey(namespace, siteId)) === "1";
  } catch {
    return false;
  }
}
function clearDismissed(namespace: string, siteId: string): void {
  try {
    sessionStorage.removeItem(dismissKey(namespace, siteId));
  } catch {
    /* ignore */
  }
}

function detectMobile(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
}

export async function init(config: CsteerConfig): Promise<CsteerInstance> {
  const cfg = normalizeConfig(config);
  const api = new ApiClient(cfg.apiUrl, cfg.siteId);
  const storage = new ConsentStorage(cfg.namespace, cfg.siteId);

  // Inline settings short-circuit the network call (used by the editor preview).
  let rawSettings: BannerSettings | null;
  try {
    rawSettings = cfg.settings ?? (await api.fetchSettings());
  } catch (err) {
    if (err instanceof DomainNotAuthorizedError) {
      // Snippet running on a domain that isn't declared for this site:
      // hide the banner entirely and log a specific, actionable message.
      const host = typeof location !== "undefined" ? location.hostname : "?";
      console.error(
        `[csteer] Bannière désactivée : le domaine « ${host} » n'est pas autorisé pour le site ${cfg.siteId}. Déclarez ce domaine dans votre tableau de bord Consenteer (https://my.consenteer.com) pour afficher la bannière.`,
      );
      instance = { config: cfg, settings: null, choices: null };
      return instance;
    }
    throw err;
  }
  if (!rawSettings) {
    console.warn(`[csteer] no settings found for siteId=${cfg.siteId}`);
    instance = { config: cfg, settings: null, choices: null };
    return instance;
  }

  // Geo-targeting (PRO+): resolve the visitor's zone to choose opt-in vs
  // opt-out and an optional per-scope locale. EU/EEA/UK (and unknown) are
  // always opt-in; only the rest of the world honours `nonEuMode`. Skipped in
  // editor preview (inline settings) and when the site hasn't enabled it.
  let geoOptOut = false;
  let geoLocale: string | undefined;
  if (!cfg.settings && rawSettings.geo?.enabled) {
    const geo = await api.fetchGeo();
    const inConsentZone = geo?.inConsentZone ?? true;
    if (inConsentZone) {
      geoLocale = rawSettings.geo.euLocale;
    } else {
      geoOptOut = rawSettings.geo.nonEuMode === "opt-out";
      geoLocale = rawSettings.geo.nonEuLocale;
    }
  }

  // A/B testing (PRO+): pick a sticky variant per visitor. Variant B uses an
  // alternative full design; version / poweredBy / geo are inherited from A so
  // consent versioning and account features stay consistent. The variant tags
  // banner impressions + decisions for the comparison. Skipped in editor preview.
  let variant: "A" | "B" | undefined;
  let baseSettings: BannerSettings = rawSettings;
  const exp = rawSettings.experiment;
  if (!cfg.settings && exp?.enabled && exp.variantB) {
    variant = resolveExperimentVariant(cfg.siteId, exp.split);
    if (variant === "B") {
      baseSettings = {
        ...exp.variantB,
        version: rawSettings.version,
        poweredBy: rawSettings.poweredBy,
        geo: rawSettings.geo,
        experiment: rawSettings.experiment,
        // Cookie inventory is site-level — both variants share it.
        cookies: rawSettings.cookies,
      };
    }
  }

  // Pick the appropriate localized copy of `texts` based on the visitor's
  // browser language. An explicit cfg.locale wins over the geo locale.
  const settings = applyLocalization(baseSettings, cfg.locale ?? geoLocale);
  // Legal basis surfaced to the page in `csteer:consent` events.
  const mode = geoOptOut ? "opt-out" : "opt-in";

  // Generic auto-block of recognized vendors (opt-in). Arm now (idempotent with
  // the early arm in index.ts) so injections are blocked until consent; release
  // everything if the feature is off (covers a stale early-arm from cache).
  // Never in editor preview (inline settings).
  if (!cfg.disableWrites) {
    if (settings.compliance?.autoBlockKnown) armKnownAutoBlock();
    else disarmAndRelease();
  }

  const stored = cfg.disableWrites ? null : storage.read();
  let choices: ConsentChoices | null = null;

  // Build a banner handle wired to the current settings/choices. Used both to
  // show the banner when a decision is needed AND to mount just the floating
  // reopen icon (mountCollapsed) after a silent decision (stored choice / GPC),
  // so the visitor can always reopen — and withdraw — their consent on any
  // page load. No-op icon when reopenIcon is disabled.
  function buildBanner(initialChoices: ConsentChoices) {
    return createBanner({
      siteId: cfg.siteId,
      settings,
      initialChoices,
      applyThemeToHost: (host) => applyTheme(host, settings, cfg.forceColorScheme),
      onOpen: () => config.onPromptOpen?.(),
      onClose: () => config.onPromptClose?.(),
      onCollapse: () => {
        if (!cfg.disableWrites) rememberDismissed(cfg.namespace, cfg.siteId);
      },
      onSubmit: async (newChoices) => {
        choices = newChoices;
        if (instance) instance.choices = choices;

        if (!cfg.disableWrites) {
          clearDismissed(cfg.namespace, cfg.siteId);
          const lifetimeDays = settings.layout.cookieLifetimeDays ?? 30;
          storage.write(choices, settings.version, lifetimeDays);
          updateFromChoices(choices);
          runAutoBlock(settings, choices);
          setKnownAutoBlockGrants(choices);
          purgeRefusedCookies(settings, choices);
          emitConsentEvent({ choices, source: "submit", mode });

          const event = classifyWebstat(choices, settings.categories);
          void api.submitWebstat(event, detectMobile(), variant);
          const settingsHash = await hashSettings(settings);
          void api.submitConsent({
            choices,
            consentVersion: settings.version,
            bannerSettingsHash: settingsHash,
          });
        } else {
          runAutoBlock(settings, choices);
        }

        const allAccepted = isAllAccepted(choices);
        if (allAccepted) config.onAccept?.(choices);
        else config.onReject?.(choices);
      },
    });
  }

  if (stored && !cfg.forceShow && stored.consentVersion === settings.version) {
    // User already made a choice at the current banner version — honor it
    // silently, but keep the floating reopen icon available so they can
    // revisit / withdraw it later.
    choices = stored.choices;
    updateFromChoices(choices);
    runAutoBlock(settings, choices);
    setKnownAutoBlockGrants(choices);
    purgeRefusedCookies(settings, choices);
    emitConsentEvent({ choices, source: "stored", mode });
    activeBanner = buildBanner(choices);
    activeBanner.mountCollapsed();
  } else if (!cfg.forceShow && settings.compliance?.honorGpc !== false && isGpcEnabled()) {
    // GPC (Global Privacy Control) — the visitor's browser signals that
    // they refuse cross-site tracking. We honor it by auto-denying all
    // non-essential categories without showing the banner. Required by
    // CCPA, recommended under GDPR. Stored explicit consent always wins
    // over GPC (handled by the branch above).
    choices = denyAllChoices();
    updateFromChoices(choices);
    runAutoBlock(settings, choices);
    setKnownAutoBlockGrants(choices);
    purgeRefusedCookies(settings, choices);
    emitConsentEvent({ choices, source: "gpc", mode });
    if (!cfg.disableWrites) {
      const lifetimeDays = settings.layout.cookieLifetimeDays ?? 30;
      storage.write(choices, settings.version, lifetimeDays);
      void api.submitWebstat("reject_all", detectMobile());
      const settingsHash = await hashSettings(settings);
      void api.submitConsent({
        choices,
        consentVersion: settings.version,
        bannerSettingsHash: settingsHash,
      });
    }
    config.onReject?.(choices);
    // GPC auto-denied silently — still surface the reopen icon so the visitor
    // can opt in / change their mind later.
    activeBanner = buildBanner(choices);
    activeBanner.mountCollapsed();
  } else {
    // Need to ask. If the visitor already closed the banner (X / Escape)
    // earlier in this tab session, don't re-open it on every navigation —
    // mount only the floating icon (no impression counted either).
    const dismissed =
      !cfg.forceShow && !cfg.disableWrites && isDismissedThisSession(cfg.namespace, cfg.siteId);
    if (!cfg.disableWrites && !dismissed) {
      void api.submitWebstat("impression", detectMobile(), variant);
    }

    // Opt-out region (non-EU CCPA-style): categories are ON by default. Apply
    // them now so trackers run before any explicit choice; the banner is still
    // shown so the visitor can opt out. Opt-in (default / EU) keeps everything
    // off until the visitor accepts.
    const baseChoices = geoOptOut ? allowAllChoices(settings.categories) : denyAllChoices();
    if (geoOptOut && !cfg.disableWrites) {
      updateFromChoices(baseChoices);
      runAutoBlock(settings, baseChoices);
      setKnownAutoBlockGrants(baseChoices);
      emitConsentEvent({ choices: baseChoices, source: "geo-optout", mode });
    }

    activeBanner = buildBanner(stored?.choices ?? baseChoices);
    // Preview-only: start collapsed (icon only). Click the icon to open
    // the full banner; X closes back to the icon. Used by the editor's
    // icon subsection so the user sees the floating icon on its own.
    if (cfg.previewCollapsed || dismissed) activeBanner.mountCollapsed();
    else activeBanner.mount();
  }

  instance = { config: cfg, settings, choices };
  return instance;
}

export function update(partial: Partial<CsteerConfig>): void {
  if (!instance) throw new Error("csteer.init() must be called first");
  Object.assign(instance.config, partial);
}

/**
 * Reopen the banner on the preferences view so the visitor can review or
 * change their choices. Non-destructive: the stored choice is kept (unlike
 * resetConsent, which wipes it). Intended for a site-provided "Manage my
 * cookies" link: `<a onclick="csteer.openPreferences();return false">…</a>`.
 * No-op when the banner isn't available (no settings / unauthorized domain).
 */
export function openPreferences(): void {
  activeBanner?.open("preferences");
}

export function resetConsent(): void {
  if (!instance) return;
  new ConsentStorage(instance.config.namespace, instance.config.siteId).clear();
  // Also forget a session dismissal, otherwise the re-init below would mount
  // the icon only instead of re-displaying the banner.
  clearDismissed(instance.config.namespace, instance.config.siteId);
  activeBanner?.unmount();
  // The page must not keep acting on a choice that no longer exists.
  emitConsentEvent({ choices: null, source: "reset", mode: "opt-in" });
  // Re-run init to re-display the banner.
  void init(instance.config);
}

export function getConsentChoice(category: keyof ConsentChoices): boolean | null {
  if (!instance?.choices) return null;
  return instance.choices[category] ?? null;
}

export function getAcceptedConsents(): ConsentChoices | null {
  return instance?.choices ?? null;
}

export function getInstance(): CsteerInstance | null {
  return instance;
}

function classifyWebstat(
  choices: ConsentChoices,
  categories?: BannerSettings["categories"],
): WebstatEvent {
  // Only the categories the site actually OFFERS count: a disabled category
  // is never granted (cf. allowAllChoices), and it must not turn a full
  // acceptance into "partial" in the stats.
  const offered = (["analytics", "marketing", "personalization", "social"] as const).filter(
    (key) => categories?.[key] !== false,
  );
  const flags = offered.map((key) => choices[key]);
  if (flags.every((v) => v)) return "accept_all";
  if (flags.every((v) => !v)) return "reject_all";
  return "partial";
}

function isAllAccepted(choices: ConsentChoices): boolean {
  return choices.analytics && choices.marketing && choices.personalization && choices.social;
}

/**
 * Returns true when the visitor's browser is sending the Global Privacy
 * Control signal. Spec: https://globalprivacycontrol.org/
 *
 * Per the spec, the value MUST be a boolean true (not truthy strings),
 * which is what we check here to avoid false positives on extensions
 * that set the property to "1" or "true".
 */
function isGpcEnabled(): boolean {
  if (typeof navigator === "undefined") return false;
  const gpc = (navigator as Navigator & { globalPrivacyControl?: unknown }).globalPrivacyControl;
  return gpc === true;
}

/** No-op when the site owner has opted out of auto-blocking. */
function runAutoBlock(settings: BannerSettings, choices: ConsentChoices): void {
  if (settings.compliance?.autoBlock === false) return;
  activateBlockedScripts(choices);
}

/**
 * Pick the appropriate localized `texts` block for the visitor and return
 * a shallow-cloned settings object that points to it.
 *
 *   Locale resolution order:
 *     1. explicit cfg.locale (host page override)
 *     2. document.documentElement.lang
 *     3. navigator.language
 *
 *   For each candidate we try the exact 2..5 char tag, then the 2-letter
 *   prefix. The first one that exists in `settings.localizations` wins;
 *   if none match, we use the primary `texts` (already loaded).
 */
function applyLocalization(
  settings: BannerSettings,
  explicitLocale: string | undefined,
): BannerSettings {
  const locs = settings.localizations;
  if (!locs || Object.keys(locs).length === 0) return settings;

  const candidates: string[] = [];
  const push = (raw: string | null | undefined) => {
    if (!raw) return;
    const tag = raw.toLowerCase();
    candidates.push(tag);
    const prefix = tag.split("-")[0];
    if (prefix && prefix !== tag) candidates.push(prefix);
  };
  push(explicitLocale);
  if (typeof document !== "undefined") push(document.documentElement.lang);
  if (typeof navigator !== "undefined") push(navigator.language);

  const primary = settings.language?.toLowerCase();
  for (const tag of candidates) {
    if (tag === primary) return settings; // primary already loaded
    const match = locs[tag];
    if (match) return { ...settings, texts: match };
  }
  return settings;
}

function denyAllChoices(): ConsentChoices {
  return {
    necessary: true,
    analytics: false,
    marketing: false,
    personalization: false,
    social: false,
  };
}

/** Grant only what the site offers — categories disabled in the site's
 *  config are never disclosed to the visitor, so never granted. */
function allowAllChoices(categories?: BannerSettings["categories"]): ConsentChoices {
  return {
    necessary: true,
    analytics: categories?.analytics !== false,
    marketing: categories?.marketing !== false,
    personalization: categories?.personalization !== false,
    social: categories?.social !== false,
  };
}

/**
 * Pick the A/B variant for this visitor. Sticky: the choice is stored in
 * localStorage so the same visitor always sees the same variant. `split` is the
 * percentage of traffic shown variant B. Falls back to an ephemeral random pick
 * when storage is unavailable.
 */
function resolveExperimentVariant(siteId: string, split: number): "A" | "B" {
  const key = `csteer:exp:${siteId}`;
  try {
    const stored = localStorage.getItem(key);
    if (stored === "A" || stored === "B") return stored;
    const pick: "A" | "B" = Math.random() * 100 < split ? "B" : "A";
    localStorage.setItem(key, pick);
    return pick;
  } catch {
    return Math.random() * 100 < split ? "B" : "A";
  }
}
